<template>
  <div
    :id="id"
    class="r-input"
    :class="{
      'has-focus': hasFocus,
      'has-value': !!model,
      disable,
      [feedback]: !!feedback,
      'no-close-icon': noCloseIcon,
      'is-search': isSearch
    }"
  >
    <RLabel v-if="!noLabel" v-bind="{ hasFocus, disable }">{{ label }}</RLabel>

    <div class="input-component">
      <q-input
        ref="input"
        v-model="model"
        :disable="disable"
        :debounce="debounce"
        :placeholder="hasFocus ? '' : placeholder || (isDate && settings.date_format)"
        :autofocus="autofocus"
        :readonly="isDate"
        :mask="mask"
        :fill-mask="fillMask"
        outlined
        dense
        @focus="hasFocus = true"
        @blur="hasFocus = false"
        @click="onClick"
        v-on="$listeners"
      >
        <template #prepend>
          <SearchIcon v-if="isSearch && (!!model || hasFocus)" dense />
        </template>

        <template #append>
          <q-icon
            v-if="isDate"
            :class="{ 'q-pr-sm': !model }"
            color="secondary-1"
            size="18px"
            name="event"
            class="cursor-pointer"
          >
            <q-popup-proxy v-model="datePickerOpen" transition-show="scale" transition-hide="scale">
              <q-date v-model="model" :mask="settings.date_format" minimal @input="datePickerOpen = false" />
            </q-popup-proxy>
          </q-icon>

          <CloseIcon v-if="!!model && !noCloseIcon && !loading" dense :disable="disable" @click="clearModel" />
          <SearchIcon v-if="isSearch && !model && !hasFocus && !loading" />

          <IconWrapper v-if="loading">
            <q-spinner color="secondary-2" size="18px" />
          </IconWrapper>

          <slot name="append-more" />
        </template>

        <template v-for="(fn, scopedSlotName) in $scopedSlots" v-slot:[scopedSlotName]="props">
          <slot :props="props" :name="scopedSlotName" />
        </template>
      </q-input>

      <div v-if="!noFeedbackIcon" class="feedback-icon">
        <template v-if="!disable">
          <ErrorIcon v-if="feedback === 'error'" />
          <WarningIcon v-if="feedback === 'warning'" />
          <DoneIcon v-if="feedback === 'success'" />
        </template>
      </div>
    </div>

    <RHint v-if="!noHint" v-bind="{ feedback }">{{ hint }}</RHint>
  </div>
</template>

<script>
export default {
  components: {
    ErrorIcon: () => import('@/ui/icons/ErrorIcon'),
    WarningIcon: () => import('@/ui/icons/WarningIcon'),
    DoneIcon: () => import('@/ui/icons/DoneIcon'),
    CloseIcon: () => import('@/ui/icons/CloseIcon'),
    IconWrapper: () => import('@/ui/icons/IconWrapper'),
    SearchIcon: () => import('@/ui/icons/SearchIcon'),
    RLabel: () => import('@/ui/shared/RLabel'),
    RHint: () => import('@/ui/shared/RHint')
  },

  inheritAttrs: false,

  props: {
    feedback: {
      type: String,
      validator: val => !val || ['error', 'warning', 'success'].includes(val)
    },

    /**
     *  What should happen when the close icon gets clicked
     *  by default the input loses focus (blur)
     */
    onClear: {
      type: String,
      validator: val => ['blur', 'focus'].includes(val),
      default: 'blur'
    },

    value: { required: true },
    mask: String,
    fillMask: [Boolean, String],
    debounce: Number,
    isSearch: Boolean,
    isDate: Boolean,
    placeholder: String,
    label: String,
    hint: String,
    noLabel: Boolean,
    noHint: Boolean,
    noFeedbackIcon: Boolean,
    noCloseIcon: Boolean,
    loading: Boolean,
    disable: Boolean,
    autofocus: Boolean,
    id: String
  },

  data: () => ({
    hasFocus: false,
    datePickerOpen: false
  }),

  computed: {
    model: {
      get() {
        return this.value
      },
      set(newVal) {
        this.$emit('input', newVal)
      }
    }
  },

  methods: {
    clearModel() {
      this.$refs.input[this.onClear]()
      this.model = null
      this.$emit('clear')
    },

    focus() {
      this.$refs.input.focus()
    },

    blur() {
      this.$refs.input.blur()
    },

    select() {
      this.$refs.input.select()
    },

    onClick() {
      if (this.isDate) this.datePickerOpen = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/fonts';
@import '~@/styles/mixins';

.r-input {
  --prependIconWidth: calc(var(--r-icon-size) + var(--r-space-xs) * 3);

  .input-component {
    display: flex;

    > label.q-field {
      width: 100%;
    }
  }

  @include q-field__control {
    height: var(--r-field-height);
    min-height: var(--r-field-height) !important;
    background: var(--r-white-1);
    border: 1px solid var(--r-white-5);
    padding: 0 !important;

    &:before,
    &:after {
      display: none !important;
    }

    .q-field__prepend,
    .q-field__append {
      height: var(--r-field-height);
      padding: 0 !important;
    }

    .q-field__prepend {
      &:empty {
        width: var(--r-space-md);
      }

      &:not(:empty) {
        width: var(--prependIconWidth);
      }
    }

    &:hover {
      border: 1px solid var(--r-secondary-4);
    }

    input {
      height: calc(var(--r-field-height) - 2px);
      @extend .text-body-1;
      color: var(--r-secondary-1);

      &::placeholder {
        @extend .text-caption-3;
        color: var(--r-grey-2) !important;
        opacity: 1 !important;
      }
    }
  }

  &.is-search {
    @include q-field__control {
      input {
        padding-right: calc(var(--prependIconWidth) - var(--r-space-md));
      }
    }
  }

  &:not(.no-close-icon) {
    @include q-field__control {
      .q-field__append:empty {
        display: unset;
        width: calc(var(--r-icon-size) + var(--r-space-sm) * 2);
      }
    }
  }

  &.has-focus {
    @include q-field__control {
      border: 1px solid var(--r-secondary-4);

      input {
        color: var(--r-secondary-2);
        padding-right: 0;
      }
    }
  }

  &.has-value {
    @include q-field__control {
      input {
        padding-right: 0;
      }
    }
  }

  &.error,
  &.warning,
  &.success {
    @include q-field__control {
      background: var(--r-white-1);
    }
  }

  &.error {
    @include q-field__control {
      border: 1px solid var(--r-danger-1);
    }
  }

  &.warning {
    @include q-field__control {
      border: 1px solid var(--r-warning-1);
    }
  }

  &.success {
    @include q-field__control {
      border: 1px solid var(--r-success-1);
    }
  }

  .feedback-icon {
    width: calc(var(--r-space-md) + var(--r-space-sm) * 2);
  }

  &.disable {
    @include q-field__control {
      background: rgba(var(--r-white-1-rgb), 0.65);
      border: 1px solid var(--r-white-3);

      > div {
        opacity: 1 !important;
      }

      input {
        color: var(--r-white-5);

        &::placeholder {
          color: var(--r-white-5) !important;
        }
      }
    }
  }
}
</style>

<docs>
### Input

```jsx
<template>
  <Grid gaf="column" justify-content="center" align-items="center" align-content="center" gap="15px">
    <RInput
      v-model="value"
      :is-search="isSearch"
      :placeholder="placeholder"
      :label="label"
      :hint="hint"
      :feedback="feedback"
      :no-label="noLabel"
      :no-hint="noHint"
      :no-feedback-icon="noFeedbackIcon"
      :no-close-icon="noCloseIcon"
      :loading="loading"
      :disable="disable"
      style="align-self: start; background: rgba(253, 246, 246, 0.7)"
    />

    <Grid justify-content="center" align-items="center" gap="10px">
      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Is search</span>
        <q-checkbox v-model="isSearch" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Feedback</span>
        <q-select v-model="feedback" dense outlined :options="feedbackOptions" emit-value map-options></q-select>
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Placeholder</span>
        <q-input v-model="placeholder" dense outlined></q-input>
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Label</span>
        <q-input v-model="label" dense outlined></q-input>
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Hint</span>
        <q-input v-model="hint" dense outlined></q-input>
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Remove label</span>
        <q-checkbox v-model="noLabel" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Remove hint</span>
        <q-checkbox v-model="noHint" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Remove feedback icon</span>
        <q-checkbox v-model="noFeedbackIcon" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Remove close icon</span>
        <q-checkbox v-model="noCloseIcon" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Loading</span>
        <q-checkbox v-model="loading" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Disable</span>
        <q-checkbox v-model="disable" dense />
      </Grid>
    </Grid>
  </Grid>
</template>

<script>
  export default {
    data: () => ({
      value: '',
      label: '',
      placeholder: '',
      hint: '',
      feedback: '',
      feedbackOptions: [
        { label: 'None', value: '' },
        { label: 'Error', value: 'error' },
        { label: 'Warning', value: 'warning' },
        { label: 'Success', value: 'success' }
      ],
      noHint: false,
      noLabel: false,
      noFeedbackIcon: false,
      noCloseIcon: false,
      loading: false,
      isSearch: false,
      disable: false
    })
  }
</script>
```
</docs>
